import firebase from '../config';
import ExamCacheService from './ExamCacheService';

/**
 * Optimized Exam Search Service
 * Provides cost-efficient and high-performance search for exam cloning
 * Features: Smart caching, pagination, fuzzy search, metadata-only queries
 */
class OptimizedExamSearchService {
  static SEARCH_CONFIG = {
    METADATA_CACHE_KEY: 'examMetadataCache',
    METADATA_CACHE_DURATION: 30 * 60 * 1000, // 30 minutes
    SEARCH_DEBOUNCE_MS: 200,
    MAX_RESULTS_PER_PAGE: 20,
    FUZZY_SEARCH_THRESHOLD: 0.6,
    RECENT_SEARCHES_KEY: 'recentExamSearches',
    MAX_RECENT_SEARCHES: 10
  };

  /**
   * Get exam metadata only (lightweight) from Firebase
   * This reduces cost by fetching only essential fields
   */
  static async fetchExamMetadata(forceRefresh = false) {
    try {
      // Check cache first
      if (!forceRefresh) {
        const cached = this.getMetadataCache();
        if (cached) {
          console.log('📖 Using cached exam metadata');
          return cached;
        }
      }

      console.log('🌐 Fetching exam metadata from Firebase...');
      
      // Fetch only metadata fields to reduce cost
      const examsRef = firebase.database().ref('TestPapers');
      const snapshot = await examsRef.once('value');
      const examsData = snapshot.val();

      if (!examsData) {
        return [];
      }

      // Extract only metadata to minimize data transfer and storage
      const metadata = Object.entries(examsData)
        .map(([code, data]) => ({
          code,
          title: data.title || data.examTitle || 'Untitled Exam',
          timer: data.timer || 60,
          created: data.created || Date.now(),
          dynamic: data.dynamic || false,
          sectionsCount: data.sections ? data.sections.length : 0,
          questionsCount: data.sections ? 
            data.sections.reduce((total, section) => 
              total + ((section.q || section.questions || []).length), 0) : 0,
          startDate: data.start || data.startDate,
          endDate: data.end || data.endDate,
          totalWeightage: data.sections ? 
            data.sections.reduce((total, section) => 
              total + (section.w || section.weightage || 0), 0) : 100,
          // Add searchable text for better search performance
          searchText: `${data.title || data.examTitle || ''} ${code}`.toLowerCase()
        }))
        .filter(exam => exam.title && exam.title !== 'Untitled Exam')
        .sort((a, b) => (b.created || 0) - (a.created || 0));

      // Cache the metadata
      this.setMetadataCache(metadata);
      
      console.log(`💾 Exam metadata cached: ${metadata.length} exams`);
      return metadata;

    } catch (error) {
      console.error('Error fetching exam metadata:', error);
      
      // Fallback to cache if available
      const cached = this.getMetadataCache();
      if (cached) {
        console.log('🔄 Using cached metadata as fallback');
        return cached;
      }
      
      throw error;
    }
  }

  /**
   * Advanced search with multiple criteria and fuzzy matching
   */
  static searchExams(examList, query, options = {}) {
    if (!query || !query.trim()) {
      return [];
    }

    const {
      fuzzySearch = true,
      searchFields = ['title', 'code'],
      maxResults = this.SEARCH_CONFIG.MAX_RESULTS_PER_PAGE,
      sortBy = 'relevance', // 'relevance', 'created', 'title', 'timer'
      filters = {}
    } = options;

    const normalizedQuery = query.toLowerCase().trim();
    const queryWords = normalizedQuery.split(/\s+/).filter(word => word.length > 0);

    let results = examList.map(exam => {
      let score = 0;
      let matches = [];

      // Exact code match gets highest priority
      if (exam.code.toLowerCase() === normalizedQuery) {
        score += 100;
        matches.push('code-exact');
      }
      // Partial code match
      else if (exam.code.toLowerCase().includes(normalizedQuery)) {
        score += 80;
        matches.push('code-partial');
      }

      // Title matching with word-based scoring
      const titleLower = exam.title.toLowerCase();
      queryWords.forEach(word => {
        if (titleLower.includes(word)) {
          // Longer words get higher scores
          const wordScore = Math.min(word.length * 2, 20);
          score += wordScore;
          matches.push(`title-${word}`);
        }
      });

      // Exact title match bonus
      if (titleLower === normalizedQuery) {
        score += 50;
        matches.push('title-exact');
      }

      // Fuzzy search for title if enabled
      if (fuzzySearch && score === 0) {
        const fuzzyScore = this.calculateFuzzyScore(titleLower, normalizedQuery);
        if (fuzzyScore > this.SEARCH_CONFIG.FUZZY_SEARCH_THRESHOLD) {
          score += fuzzyScore * 30;
          matches.push('fuzzy-title');
        }
      }

      // Apply filters
      if (filters.dynamic !== undefined && exam.dynamic !== filters.dynamic) {
        score = 0;
      }
      if (filters.minTimer && exam.timer < filters.minTimer) {
        score = 0;
      }
      if (filters.maxTimer && exam.timer > filters.maxTimer) {
        score = 0;
      }

      return {
        ...exam,
        searchScore: score,
        searchMatches: matches
      };
    }).filter(exam => exam.searchScore > 0);

    // Sort results
    switch (sortBy) {
      case 'relevance':
        results.sort((a, b) => b.searchScore - a.searchScore);
        break;
      case 'created':
        results.sort((a, b) => (b.created || 0) - (a.created || 0));
        break;
      case 'title':
        results.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'timer':
        results.sort((a, b) => a.timer - b.timer);
        break;
      default:
        results.sort((a, b) => b.searchScore - a.searchScore);
    }

    // Limit results
    return results.slice(0, maxResults);
  }

  /**
   * Simple fuzzy search algorithm
   */
  static calculateFuzzyScore(text, pattern) {
    if (text.length === 0) return pattern.length === 0 ? 1 : 0;
    if (pattern.length === 0) return 1;

    const textLen = text.length;
    const patternLen = pattern.length;
    
    // Create matrix for dynamic programming
    const matrix = Array(textLen + 1).fill().map(() => Array(patternLen + 1).fill(0));
    
    // Initialize first row and column
    for (let i = 0; i <= textLen; i++) matrix[i][0] = i;
    for (let j = 0; j <= patternLen; j++) matrix[0][j] = j;
    
    // Fill matrix
    for (let i = 1; i <= textLen; i++) {
      for (let j = 1; j <= patternLen; j++) {
        if (text[i - 1] === pattern[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,    // deletion
            matrix[i][j - 1] + 1,    // insertion
            matrix[i - 1][j - 1] + 1 // substitution
          );
        }
      }
    }
    
    // Calculate similarity score (0-1)
    const distance = matrix[textLen][patternLen];
    const maxLen = Math.max(textLen, patternLen);
    return 1 - (distance / maxLen);
  }

  /**
   * Get full exam details for selected exam (with caching)
   */
  static async getExamDetails(examCode) {
    try {
      // Check cache first
      const cached = ExamCacheService.getExamCache(examCode);
      if (cached) {
        console.log(`📖 Using cached exam details: ${examCode}`);
        return cached;
      }

      console.log(`🌐 Fetching exam details from Firebase: ${examCode}`);
      const examRef = firebase.database().ref(`TestPapers/${examCode}`);
      const snapshot = await examRef.once('value');
      const examData = snapshot.val();

      if (!examData) {
        throw new Error(`Exam not found: ${examCode}`);
      }

      // Cache the full exam data
      ExamCacheService.setExamCache(examCode, examData);

      return examData;
    } catch (error) {
      console.error(`Error fetching exam details for ${examCode}:`, error);
      throw error;
    }
  }

  /**
   * Cache metadata
   */
  static setMetadataCache(metadata) {
    try {
      const cacheData = {
        data: metadata,
        timestamp: Date.now(),
        count: metadata.length
      };
      
      localStorage.setItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.warn('Failed to cache metadata:', error);
      return false;
    }
  }

  /**
   * Get cached metadata
   */
  static getMetadataCache() {
    try {
      const cached = localStorage.getItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      const now = Date.now();
      const cacheAge = now - (parsed.timestamp || 0);

      if (cacheAge < this.SEARCH_CONFIG.METADATA_CACHE_DURATION) {
        return parsed.data;
      }

      // Cache expired
      localStorage.removeItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY);
      return null;
    } catch (error) {
      console.warn('Failed to get metadata cache:', error);
      localStorage.removeItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY);
      return null;
    }
  }

  /**
   * Save recent search query
   */
  static saveRecentSearch(query) {
    try {
      if (!query || query.trim().length < 2) return;

      const recent = this.getRecentSearches();
      const updated = [query.trim(), ...recent.filter(q => q !== query.trim())]
        .slice(0, this.SEARCH_CONFIG.MAX_RECENT_SEARCHES);

      localStorage.setItem(this.SEARCH_CONFIG.RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch (error) {
      console.warn('Failed to save recent search:', error);
    }
  }

  /**
   * Get recent search queries
   */
  static getRecentSearches() {
    try {
      const recent = localStorage.getItem(this.SEARCH_CONFIG.RECENT_SEARCHES_KEY);
      return recent ? JSON.parse(recent) : [];
    } catch (error) {
      console.warn('Failed to get recent searches:', error);
      return [];
    }
  }

  /**
   * Clear metadata cache
   */
  static clearMetadataCache() {
    try {
      localStorage.removeItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY);
      console.log('🗑️ Metadata cache cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear metadata cache:', error);
      return false;
    }
  }

  /**
   * Get search suggestions based on recent searches and exam titles
   */
  static getSearchSuggestions(query, examList, limit = 5) {
    if (!query || query.length < 1) {
      return this.getRecentSearches().slice(0, limit);
    }

    const suggestions = new Set();
    const normalizedQuery = query.toLowerCase();

    // Add matching exam titles
    examList.forEach(exam => {
      if (exam.title.toLowerCase().includes(normalizedQuery)) {
        suggestions.add(exam.title);
      }
      if (exam.code.toLowerCase().includes(normalizedQuery)) {
        suggestions.add(exam.code);
      }
    });

    // Add matching recent searches
    this.getRecentSearches().forEach(recentQuery => {
      if (recentQuery.toLowerCase().includes(normalizedQuery)) {
        suggestions.add(recentQuery);
      }
    });

    return Array.from(suggestions).slice(0, limit);
  }

  /**
   * Get cache statistics for monitoring
   */
  static getCacheStats() {
    try {
      const metadataCache = localStorage.getItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY);
      const recentSearches = localStorage.getItem(this.SEARCH_CONFIG.RECENT_SEARCHES_KEY);
      
      const stats = {
        metadataCache: {
          exists: !!metadataCache,
          size: metadataCache ? new Blob([metadataCache]).size : 0,
          count: 0,
          valid: false
        },
        recentSearches: {
          exists: !!recentSearches,
          size: recentSearches ? new Blob([recentSearches]).size : 0,
          count: 0
        }
      };

      if (metadataCache) {
        try {
          const parsed = JSON.parse(metadataCache);
          stats.metadataCache.count = parsed.count || 0;
          stats.metadataCache.valid = (Date.now() - parsed.timestamp) < this.SEARCH_CONFIG.METADATA_CACHE_DURATION;
        } catch (e) {
          // Invalid cache data
        }
      }

      if (recentSearches) {
        try {
          const parsed = JSON.parse(recentSearches);
          stats.recentSearches.count = parsed.length || 0;
        } catch (e) {
          // Invalid cache data
        }
      }

      return stats;
    } catch (error) {
      console.warn('Failed to get search cache stats:', error);
      return null;
    }
  }

  /**
   * Clear all search-related caches
   */
  static clearAllSearchCaches() {
    try {
      localStorage.removeItem(this.SEARCH_CONFIG.METADATA_CACHE_KEY);
      localStorage.removeItem(this.SEARCH_CONFIG.RECENT_SEARCHES_KEY);
      console.log('🗑️ All search caches cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear search caches:', error);
      return false;
    }
  }

  /**
   * Advanced filter options
   */
  static applyAdvancedFilters(examList, filters) {
    return examList.filter(exam => {
      // Dynamic/Legacy filter
      if (filters.examType && filters.examType !== 'all') {
        if (filters.examType === 'dynamic' && !exam.dynamic) return false;
        if (filters.examType === 'legacy' && exam.dynamic) return false;
      }

      // Timer range filter
      if (filters.minTimer && exam.timer < filters.minTimer) return false;
      if (filters.maxTimer && exam.timer > filters.maxTimer) return false;

      // Sections count filter
      if (filters.minSections && exam.sectionsCount < filters.minSections) return false;
      if (filters.maxSections && exam.sectionsCount > filters.maxSections) return false;

      // Questions count filter
      if (filters.minQuestions && exam.questionsCount < filters.minQuestions) return false;
      if (filters.maxQuestions && exam.questionsCount > filters.maxQuestions) return false;

      // Date range filter
      if (filters.createdAfter && exam.created < filters.createdAfter) return false;
      if (filters.createdBefore && exam.created > filters.createdBefore) return false;

      return true;
    });
  }

  /**
   * Get popular/frequently cloned exams
   */
  static getPopularExams(examList, limit = 10) {
    // For now, return most recent exams
    // In future, this could be enhanced with actual clone frequency data
    return examList
      .sort((a, b) => (b.created || 0) - (a.created || 0))
      .slice(0, limit);
  }

  /**
   * Create debounced search function
   */
  static createDebouncedSearch(searchFunction, delay = this.SEARCH_CONFIG.SEARCH_DEBOUNCE_MS) {
    let timeoutId;
    
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => searchFunction(...args), delay);
    };
  }

  /**
   * Format exam for display in search results
   */
  static formatExamForDisplay(exam) {
    return {
      ...exam,
      displayTitle: exam.title,
      displaySubtitle: `${exam.code} • ${exam.timer}min • ${exam.sectionsCount} sections • ${exam.questionsCount} questions`,
      typeLabel: exam.dynamic ? 'Dynamic' : 'Legacy',
      createdFormatted: exam.created ? new Date(exam.created).toLocaleDateString() : 'Unknown'
    };
  }

  /**
   * Validate search query
   */
  static validateSearchQuery(query) {
    if (!query || typeof query !== 'string') {
      return { valid: false, error: 'Query must be a string' };
    }

    if (query.trim().length === 0) {
      return { valid: false, error: 'Query cannot be empty' };
    }

    if (query.trim().length > 100) {
      return { valid: false, error: 'Query too long (max 100 characters)' };
    }

    // Check for potentially harmful patterns
    const harmfulPatterns = [/<script/i, /javascript:/i, /data:text\/html/i];
    if (harmfulPatterns.some(pattern => pattern.test(query))) {
      return { valid: false, error: 'Invalid query pattern' };
    }

    return { valid: true };
  }
}

export default OptimizedExamSearchService; 